import { useMemo } from 'react';
import { countryToISO, isSupportedCurrency, SubPartnerAccount, TransactionsLists } from 'vopay';
import { TransactionsHistoryHostProps } from 'shared/transactionHistory';
import { ACCOUNTS_PATH } from '@/components/Entities/paths';

/**
 * Maps a partner account's list row onto what the shared Transaction History takes as props, for the
 * partner page's Account Transactions tab (ID-13796), where the list shows every transaction of every
 * account the partner account created. The list speaks `/account/transactions`; here the rows come from
 * `/partner/account/transactions`, signed as the partner account, which filters by account, date, type,
 * status and currency only — so the bar leaves off the filters it cannot honour (and, for this source,
 * adds the Account filter itself), the table the columns the route never fills, and the export menu the
 * PDF it does not render.
 */
export default function usePartnerTransactionsHostProps(partner: SubPartnerAccount): TransactionsHistoryHostProps {
  return useMemo(
    () => ({
      // The partner portal has no timezone setting to read, so the list keeps its own default (UTC).
      viewer: { timezone: '' },
      account: {
        // The row sends the country as its display name; the shared filter vocabulary keys on ISO.
        country: countryToISO(partner.Country ?? ''),
        // The route already answers for every account under the partner account, sub-accounts included;
        // its own sub-account flag only narrows that set, so the checkbox would mislead.
        subAccountPermissions: false,
        // A partner account has no balances of its own (spec 5.5): the currencies its accounts hold are
        // the ones on the list row's balance roll-up.
        currencies: Object.keys(partner.TotalBalances).filter(isSupportedCurrency),
      },
      // Viewing is never role-gated on the partner side. Clearing a flag is an account route, which a
      // partner account's token cannot call, so the action is not offered.
      userPermissions: { canViewSubaccounts: true, canRemoveFlags: false },
      hostPaths: {
        // The account page's transaction page: its bearer lookup (`/partner/account?VopayAccountID`) answers
        // for a child partner's accounts too. The rail segment is the type with its spaces removed; the
        // type, the account name and the row's date ride on the query as the account page's list writes them.
        transactionDetail: (transaction: TransactionsLists) => {
          const search = new URLSearchParams({
            type: transaction.TransactionType,
            accountName: transaction.AccountName,
            date: transaction.TransactionDateTime,
          }).toString();

          return `${ACCOUNTS_PATH}/${transaction.AccountID}/${transaction.TransactionType.replaceAll(' ', '')}/transaction/${transaction.TransactionID}?${search}`;
        },
      },
      // The list spans accounts, so the column that tells them apart is not tied to the sub-account filter.
      showAccountName: true,
      // The keyword stays: the route takes it as a transaction ID.
      hiddenFilters: ['timeZone', 'flagged', 'settlementDate'],
      hiddenColumns: ['paymentRailID', 'settlementDate'],
      hiddenExports: ['pdf'],
      // The partner account's whole book; PartnerScopedApiProvider signs the call with its token.
      transactionsSource: 'partnerAccount',
    }),
    [partner],
  );
}
